import { useState, type FormEvent } from "react";
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Input,
  Modal,
  ModalFooter,
} from "@insights/ui";
import { Github, Mail, Pencil, Tag, Trash2, X } from "lucide-react";
import { useParams } from "react-router";
import { authClient, json } from "../../../api";
import { Loading, PageHeader } from "../../../shared/components";
import { AppShell } from "../../../shared/layout";
import { RoleCombobox } from "../components/RoleCombobox";
import {
  canAdministerWorkspace,
  canRemoveWorkspaceMember,
  canRenameWorkspaceMember,
  isLastWorkspaceOwner,
} from "../helpers/member-permissions";
import { useAdministration } from "../hooks/useAdministration";
import type {
  Administration,
  Invitation,
  Team,
  WorkspaceMember,
} from "../types/administration";

export function TeamsPage() {
  const { workspaceId } = useParams();
  const session = authClient.useSession();
  const { query, mutate } = useAdministration(workspaceId);
  const [email, setEmail] = useState("");
  const [actionError, setActionError] = useState("");
  const [newTeamName, setNewTeamName] = useState("");
  const [createTeamError, setCreateTeamError] = useState("");
  const [renamingTeam, setRenamingTeam] = useState<Team | null>(null);
  const [teamName, setTeamName] = useState("");
  const [teamError, setTeamError] = useState("");
  const [savingTeam, setSavingTeam] = useState(false);
  const [renamingMember, setRenamingMember] = useState<WorkspaceMember | null>(
    null,
  );
  const [memberName, setMemberName] = useState("");
  const [renameError, setRenameError] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [mappingMember, setMappingMember] = useState<WorkspaceMember | null>(null);
  const [githubLogin, setGithubLogin] = useState("");
  const [mappingError, setMappingError] = useState("");
  const [savingMapping, setSavingMapping] = useState(false);

  function openGithubMapping(member: WorkspaceMember) {
    setMappingMember(member);
    setGithubLogin(member.githubLogin ?? "");
    setMappingError("");
  }

  async function saveGithubMapping(event: FormEvent) {
    event.preventDefault();
    if (!mappingMember) {
      return;
    }
    setSavingMapping(true);
    setMappingError("");
    try {
      await mutate(
        `/${workspaceId}/members/${mappingMember.userId}/github-identity`,
        {
          method: "PUT",
          body: JSON.stringify({ login: githubLogin.trim() || null }),
        },
      );
      setMappingMember(null);
    } catch (error) {
      setMappingError(
        error instanceof Error ? error.message : "Unable to map GitHub login",
      );
    } finally {
      setSavingMapping(false);
    }
  }

  async function cancelInvitation(invitation: Invitation) {
    if (!confirm(`Cancel the invitation for ${invitation.email}?`)) {
      return;
    }
    await mutate(`/${workspaceId}/invitations/${invitation.id}`, {
      method: "DELETE",
    });
  }

  async function removeMember(member: WorkspaceMember) {
    if (!confirm(`Remove ${member.email} from this workspace?`)) {
      return;
    }
    setActionError("");
    try {
      await mutate(`/${workspaceId}/members/${member.id}`, {
        method: "DELETE",
      });
    } catch (error) {
      setActionError(
        error instanceof Error ? error.message : "Unable to remove member",
      );
    }
  }

  function openRenameMember(member: WorkspaceMember) {
    setRenamingMember(member);
    setMemberName(member.name);
    setRenameError("");
  }

  async function renameMember(event: FormEvent) {
    event.preventDefault();
    if (!renamingMember) {
      return;
    }
    const name = memberName.trim();
    if (!name) {
      setRenameError("Enter a name for this workspace.");
      return;
    }
    if (name === renamingMember.name) {
      setRenamingMember(null);
      return;
    }
    setRenameError("");
    setRenaming(true);
    try {
      await mutate(`/${workspaceId}/members/${renamingMember.id}/name`, {
        method: "PATCH",
        body: JSON.stringify({ name }),
      });
      setRenamingMember(null);
    } catch (error) {
      setRenameError(
        error instanceof Error
          ? error.message
          : "Unable to change workspace name",
      );
    } finally {
      setRenaming(false);
    }
  }

  async function createTeam(event: FormEvent) {
    event.preventDefault();
    const name = newTeamName.trim();
    if (!name) {
      return;
    }
    setCreateTeamError("");
    try {
      await mutate(`/${workspaceId}/teams`, json({ name }));
      setNewTeamName("");
    } catch (error) {
      setCreateTeamError(
        error instanceof Error ? error.message : "Unable to create team tag",
      );
    }
  }

  function openRenameTeam(team: Team) {
    setRenamingTeam(team);
    setTeamName(team.name);
    setTeamError("");
  }

  async function saveTeam(event: FormEvent) {
    event.preventDefault();
    if (!renamingTeam) {
      return;
    }
    const name = teamName.trim();
    if (!name) {
      setTeamError("Enter a team tag name.");
      return;
    }
    if (name === renamingTeam.name) {
      setRenamingTeam(null);
      return;
    }
    setTeamError("");
    setSavingTeam(true);
    try {
      await mutate(`/${workspaceId}/teams/${renamingTeam.id}`, {
        method: "PATCH",
        body: JSON.stringify({ name }),
      });
      setRenamingTeam(null);
    } catch (error) {
      setTeamError(
        error instanceof Error ? error.message : "Unable to rename team tag",
      );
    } finally {
      setSavingTeam(false);
    }
  }

  async function deleteTeam(team: Team) {
    if (
      !confirm(
        `Delete the ${team.name} team? Members will remain in the workspace.`,
      )
    )
      return;
    setActionError("");
    try {
      await mutate(`/${workspaceId}/teams/${team.id}`, { method: "DELETE" });
    } catch (error) {
      setActionError(
        error instanceof Error ? error.message : "Unable to delete team",
      );
    }
  }

  async function toggleTeam(teamId: string, userId: string, assigned: boolean) {
    setActionError("");
    try {
      await mutate(
        `/${workspaceId}/teams/${teamId}/members${assigned ? `/${userId}` : ""}`,
        assigned ? { method: "DELETE" } : json({ userId }),
      );
    } catch (error) {
      setActionError(
        error instanceof Error
          ? error.message
          : "Unable to update team assignment",
      );
    }
  }

  if (query.isLoading) {
    return (
      <AppShell>
        <Loading />
      </AppShell>
    );
  }
  const administration = query.data as Administration;
  const canAdminister = canAdministerWorkspace(administration.role);
  const memberRoles = administration.members.map((member) => member.role);

  return (
    <AppShell>
      <PageHeader eyebrow="Workspace administration" title="Members & teams" />
      <div className="admin-grid">
        <Card className="members-card">
          <h2 className="section-card-title">Workspace members</h2>
          {canAdminister && (
            <form
              className="member-invite"
              onSubmit={async (event) => {
                event.preventDefault();
                await mutate(
                  `/${workspaceId}/invitations`,
                  json({ email, role: "viewer" }),
                );
                setEmail("");
              }}
            >
              <Input
                aria-label="Member email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="colleague@example.com"
                required
              />
              <Button icon={<Mail />}>Send invitation</Button>
            </form>
          )}
          {administration.invitations.map((invitation) => (
            <div className="row" key={invitation.id}>
              <span>{invitation.email}</span>
              <Badge className="invitation-badge">
                Pending · {invitation.role}
                {canAdminister && (
                  <ActionIcon
                    color="danger"
                    icon={<X />}
                    label={`Cancel invitation for ${invitation.email}`}
                    onClick={() => cancelInvitation(invitation)}
                    size="compact"
                  />
                )}
              </Badge>
            </div>
          ))}
          {actionError && <p className="error">{actionError}</p>}
          {administration.members.map((member) => {
            const assignedTeamIds = new Set(
              administration.teamMembers
                .filter((teamMember) => teamMember.userId === member.userId)
                .map((teamMember) => teamMember.teamId),
            );
            const canRename = canRenameWorkspaceMember(
              administration.role,
              session.data?.user.id,
              member.userId,
            );
            const isLastOwner = isLastWorkspaceOwner(
              member.role,
              memberRoles,
            );
            return (
              <div className="row member-row" key={member.id}>
                <div className="member-identity">
                  <div className="member-name">
                    <strong>{member.name}</strong>
                    {canRename && (
                      <ActionIcon
                        icon={<Pencil />}
                        label={`Change workspace name for ${member.email}`}
                        onClick={() => openRenameMember(member)}
                        size="compact"
                      />
                    )}
                  </div>
                  <span>{member.email}</span>
                  <span>{member.githubLogin ? `GitHub: @${member.githubLogin}` : "GitHub login not mapped"}</span>
                  <div className="team-tags">
                    {administration.teams.map((team) => {
                      const assigned = assignedTeamIds.has(team.id);
                      return canAdminister ? (
                        <button
                          type="button"
                          className={assigned ? "assigned" : ""}
                          aria-pressed={assigned}
                          onClick={() =>
                            toggleTeam(team.id, member.userId, assigned)
                          }
                          key={team.id}
                        >
                          {team.name}
                        </button>
                      ) : assigned ? (
                        <span className="team-tag" key={team.id}>
                          {team.name}
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>
                <div className="member-actions">
                  {canAdminister && (
                    <ActionIcon
                      icon={<Github />}
                      label={`Map GitHub login for ${member.email}`}
                      onClick={() => openGithubMapping(member)}
                    />
                  )}
                  <RoleCombobox
                    value={member.role}
                    disabled={!canAdminister || isLastOwner}
                    label={`Role for ${member.email}`}
                    onValueChange={(role) =>
                      mutate(`/${workspaceId}/members/${member.id}`, {
                        method: "PATCH",
                        body: JSON.stringify({ role }),
                      })
                    }
                  />
                  {canRemoveWorkspaceMember(
                    administration.role,
                    member.role,
                  ) && (
                    <ActionIcon
                      color="danger"
                      icon={<Trash2 />}
                      label={`Remove ${member.email} from workspace`}
                      title={
                        isLastOwner
                          ? "Add another owner before removing this member"
                          : "Remove member"
                      }
                      disabled={isLastOwner}
                      onClick={() => removeMember(member)}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </Card>
        <Card className="form-card team-card">
          <h2 className="section-card-title">Team tags</h2>
          {canAdminister && (
            <form onSubmit={createTeam}>
              <Input
                aria-label="New team tag"
                value={newTeamName}
                onChange={(event) => setNewTeamName(event.target.value)}
                placeholder="New tag name"
                maxLength={100}
                required
              />
              <Button icon={<Tag />}>Add tag</Button>
            </form>
          )}
          {createTeamError && <p className="error">{createTeamError}</p>}
          {!administration.teams.length && (
            <p className="muted">No team tags yet.</p>
          )}
          {administration.teams.map((team) => (
            <div className="row" key={team.id}>
              <div>
                <strong>{team.name}</strong>
                <span>
                  {
                    administration.teamMembers.filter(
                      (member) => member.teamId === team.id,
                    ).length
                  }{" "}
                  members
                </span>
              </div>
              {canAdminister && (
                <div className="team-actions">
                  <ActionIcon
                    icon={<Pencil />}
                    label={`Rename ${team.name}`}
                    onClick={() => openRenameTeam(team)}
                    size="small"
                  />
                  <ActionIcon
                    color="danger"
                    icon={<Trash2 />}
                    label={`Delete ${team.name}`}
                    onClick={() => deleteTeam(team)}
                    size="small"
                  />
                </div>
              )}
            </div>
          ))}
        </Card>
      </div>
      <Modal
        className="wide-modal"
        open={mappingMember !== null}
        onOpenChange={(open) => {
          if (!open && !savingMapping) {
            setMappingMember(null);
          }
        }}
        title="Map GitHub contributor"
        description="Associate this member with one GitHub login for team cohort metrics. Leave it empty to remove the mapping."
      >
        <form className="name-modal-form" onSubmit={saveGithubMapping}>
          <label className="modal-field">
            GitHub login
            <Input
              value={githubLogin}
              onChange={(event) => setGithubLogin(event.target.value)}
              placeholder="octocat"
              maxLength={39}
              autoFocus
            />
          </label>
          {mappingError && <p className="error">{mappingError}</p>}
          <ModalFooter>
            <Button type="button" color="neutral" disabled={savingMapping} onClick={() => setMappingMember(null)}>
              Cancel
            </Button>
            <Button type="submit" disabled={savingMapping}>
              {savingMapping ? "Saving…" : "Save mapping"}
            </Button>
          </ModalFooter>
        </form>
      </Modal>
      <Modal
        open={renamingTeam !== null}
        onOpenChange={(open) => {
          if (!open && !savingTeam) {
            setRenamingTeam(null);
          }
        }}
        title="Rename team tag"
        description="Change this organizational tag. Existing member assignments are preserved."
      >
        <form className="name-modal-form" onSubmit={saveTeam}>
          <label className="modal-field">
            Tag name
            <Input
              value={teamName}
              onChange={(event) => setTeamName(event.target.value)}
              placeholder="Platform"
              maxLength={100}
              autoFocus
              required
            />
          </label>
          {teamError && <p className="error">{teamError}</p>}
          <ModalFooter>
            <Button
              type="button"
              color="neutral"
              disabled={savingTeam}
              onClick={() => setRenamingTeam(null)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={savingTeam}>
              {savingTeam ? "Saving…" : "Save name"}
            </Button>
          </ModalFooter>
        </form>
      </Modal>
      <Modal
        open={renamingMember !== null}
        onOpenChange={(open) => {
          if (!open && !renaming) {
            setRenamingMember(null);
          }
        }}
        title="Change workspace name"
        description={
          renamingMember
            ? `This name is shown for ${renamingMember.email} in this workspace only.`
            : "Change the member name used in this workspace."
        }
      >
        <form className="name-modal-form" onSubmit={renameMember}>
          <label className="modal-field">
            Name
            <Input
              value={memberName}
              onChange={(event) => setMemberName(event.target.value)}
              maxLength={100}
              autoFocus
              required
            />
          </label>
          {renameError && <p className="error">{renameError}</p>}
          <ModalFooter>
            <Button
              type="button"
              color="neutral"
              disabled={renaming}
              onClick={() => setRenamingMember(null)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={renaming}>
              {renaming ? "Saving…" : "Save name"}
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    </AppShell>
  );
}
